/**
 * UnconfirmedEmailBanner — shown when the user is using the app but has not yet
 * clicked the confirmation link sent to their email address.
 *
 * Remote synchronization and social features remain paused until confirmed.
 */

import React, { useState } from 'react';
import { Mail, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { requestMagicLink } from '@yada/api-client';

interface UnconfirmedEmailBannerProps {
  email?: string;
}

export const UnconfirmedEmailBanner: React.FC<UnconfirmedEmailBannerProps> = ({ email }) => {
  const [isSending, setIsSending] = useState(false);
  const [sentSuccess, setSentSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const startCooldown = () => {
    setCooldown(60);
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (!email || cooldown > 0 || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      await requestMagicLink(email.trim().toLowerCase());
      setSentSuccess(true);
      startCooldown();
      setTimeout(() => setSentSuccess(false), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend confirmation email');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '0.625rem 1rem',
        background: 'color-mix(in srgb, var(--warning) 15%, var(--bg))',
        color: 'var(--warning)',
        fontSize: '0.875rem',
        borderBottom: '0.125rem solid color-mix(in srgb, var(--warning) 35%, transparent)',
        gap: '0.625rem',
        flexWrap: 'wrap',
      }}
      role="alert"
      aria-live="polite"
    >
      <AlertTriangle size={16} style={{ flexShrink: 0, color: 'var(--warning)' }} />
      <span style={{ flex: 1, minWidth: '15rem' }}>
        <strong>Email confirmation pending{email ? ` for ${email}` : ''}.</strong>{' '}
        Remote sync and social features are paused until you confirm your email. Your email is your primary recovery anchor.
      </span>

      {error && (
        <span style={{ color: 'var(--error)', fontSize: '0.8rem' }}>
          {error}
        </span>
      )}

      {sentSuccess ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: 'var(--accent)', fontSize: '0.8rem', fontWeight: 500 }}>
          <CheckCircle2 size={14} /> Link sent! Check inbox
        </span>
      ) : (
        <button
          type="button"
          onClick={handleResend}
          disabled={isSending || cooldown > 0 || !email}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
            padding: '0.25rem 0.625rem',
            background: 'color-mix(in srgb, var(--warning) 25%, transparent)',
            color: 'var(--warning)',
            border: '1px solid color-mix(in srgb, var(--warning) 50%, transparent)',
            borderRadius: '0.25rem',
            fontSize: '0.8rem',
            fontWeight: 500,
            cursor: isSending || cooldown > 0 || !email ? 'not-allowed' : 'pointer',
            opacity: isSending || cooldown > 0 || !email ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          <Mail size={13} />
          {cooldown > 0 ? `Resend link (${cooldown}s)` : isSending ? 'Sending...' : 'Resend confirmation email'}
        </button>
      )}
    </div>
  );
};
